/**
 * Contrato REAL del backend de Activa (:3005).
 *
 * OJO: `types.ts` es del mundo mock y declara estados en español
 * ('borrador' | 'enviando' | 'medida') que NO existen en el backend. Este archivo
 * tipa lo que la API devuelve de verdad; no mezclar los dos.
 */

/** Estados reales de una campaña (backend). "programada" = espera su hora; sale sola. */
export type EstadoCampana = "draft" | "analyzed" | "context" | "messaged" | "programada" | "enviando" | "sent" | "measured";

export interface ReportReal {
  totalClientes: number;
  plataDormida: number;
  /**
   * El cohorte dormido: cuántos son y su ticket TÍPICO por compra (mediana). Ancla en pesos del
   * techo de descuento. OPCIONAL: los reportes ya persistidos (campañas previas a esta feature) no
   * lo traen — leer siempre con `?.` / `?? 0`, o reabrir una campaña vieja tira la pantalla.
   */
  dormidos?: { cantidad: number; ticketMediano: number };
  concentracion: { pctClientes: number; pctFacturacion: number };
  baseDormida: { pct: number; meses: number };
  rotativo: { titulo: string; valor: string; detalle: string };
  sinContacto: number;
  duplicadosCelular: number;
  mesesHistoria: number;

  /* Espejo de ReportData del backend (backend/src/modules/activa/types/index.ts). Estos campos ya
     los devolvía la API y ReporteComercial.tsx ya los usaba, pero faltaban acá: `tsc` fallaba y
     como el build es `tsc && vite build`, la rama entera no compilaba. Si agregás un campo al
     reporte en el backend, agregalo también acá. */

  /** filas de pedido que entraron al análisis (con ID y fecha válidos) */
  ordenesAnalizadas: number;
  /** personas con al menos una compra (≠ totalClientes, que incluye a los que nunca compraron) */
  clientesConCompra: number;
  /** compradores sin ficha en el archivo de clientes: facturan, pero no tienen nombre ni contacto */
  huerfanos: number;
  /** órdenes cuyo importe no se pudo leer: cuentan la compra, no la plata */
  ordenesSinMonto: number;
  /** devoluciones / notas de crédito (importe negativo): no son ventas */
  ordenesNegativas: number;
  /* Las filas que no entraron y cómo se leyó la columna de fechas. OPCIONALES: los reportes ya
     guardados no los traen — leer con `?? 0` / `?.`. */
  /** filas de pedido descartadas por no traer ID del cliente */
  ordenesSinId?: number;
  /** filas de pedido descartadas porque la fecha vino vacía o no se pudo leer */
  ordenesSinFecha?: number;
  /** filas de pedido descartadas por tener una fecha posterior a hoy */
  ordenesFechaFutura?: number;
  /** cómo se leyó la columna de fechas: DMY, MDY, "mixto" (incoherente, se leyó DMY) o "sin_barras" (ISO) */
  formatoFecha?: "DMY" | "MDY" | "mixto" | "sin_barras";
  /** true = la columna no daba evidencia y se asumió día/mes */
  formatoFechaAsumido?: boolean;
  /** contra qué fecha se midió la recencia: la última orden válida del archivo ("aaaa-mm-dd", día local) */
  fechaCorte?: string;
  /** días entre esa última orden y el análisis. > 30 = archivo viejo, el reporte lo avisa */
  diasDesdeUltimaOrden?: number | null;
  /** IDs distintos repetidos en el archivo de clientes (sus filas se fusionaron). Leer con `?? 0` */
  idsDuplicados?: number;
  /** celdas Mail que no eran un mail ("-", "N/A"…) y se trataron como vacías. Leer con `?? 0` */
  mailsInvalidos?: number;
  /** facturación total de la base analizada. OJO: pesos nominales, sin ajustar por inflación */
  facturacionTotal: number;
  /** los que probaron una vez y no volvieron: la palanca más barata que existe */
  unaSolaCompra: { cantidad: number; pct: number };
  /** ritmo típico entre una compra y la siguiente (mediana, en días). null si nadie repitió */
  ritmoDias: number | null;
  /** hace cuánto son clientes: mediana de días desde la primera compra. null si no se pudo medir */
  antiguedadDias: number | null;
  /** cómo se reparte la facturación entre los grupos (suma ~100) */
  repartoIngreso: { grupoId: string; nombre: string; emoji: string; pct: number }[];
  /** el canal con mayor ticket promedio, si el archivo trae la columna. null si no vino */
  canalTop: { nombre: string; ticketPromedio: number; ordenes: number } | null;
  /** tienen mail o celular. OJO: no implica que se les pueda escribir — ver `alcanzables` */
  contactables: number;
  /** los que además dieron consentimiento: los únicos que reciben con la política estricta */
  alcanzables: number;
  /** tienen contacto pero nunca se les preguntó (≠ dijeron que no): son una tarea, no una puerta cerrada */
  sinConsentimiento: number;
  /**
   * a cuántos les puede llegar por WhatsApp oficial: celular válido y consentimiento (`llegaPorOficial`, backend).
   * Opcional: los reportes guardados antes no lo traen, y ahí el reporte no dice un número para el oficial.
   */
  alcanzablesOficial?: number;
}

export interface GrupoReal {
  id: string;
  nombre: string;
  emoji: string;
  personas: number;
  valor: number;
  perfil: string;
  campana: string;
  oferta: string;
  canal: string;
  prioridad: string;
  descuentoPct: number;
  /** el % que el motor propuso ANTES del techo; si difiere, el guardrail actuó */
  descuentoPropuesto?: number;
  /** puntos que recibe cada persona del grupo (0 = ninguno; el descuento le gana a los puntos) */
  puntosPorPersona?: number;
  mensaje?: string;
}

/** Ventana de envío. NO es decorativa: el worker corta fuera de la franja y deja pendiente el resto. */
export interface FranjaHorariaReal {
  /** "HH:MM". Si desde > hasta, cruza la medianoche (22:00 → 02:00, el horario de un bar). */
  desde: string;
  hasta: string;
  /** días permitidos (0=domingo … 6=sábado). Vacío = todos. */
  dias?: number[];
}

/** Cómo se canjea el descuento. Sin esto el mensaje ofrecía un % que nadie podía validar. */
export interface CuponConfigReal {
  /** el código que el cliente muestra en el mostrador */
  codigo?: string;
  /** días de validez contados DESDE EL ENVÍO (la fecha la resuelve el backend al mandar) */
  vigenciaDias?: number;
  montoMinimo?: number;
  exclusiones?: string;
}

/** Cómo se le habla a la persona. Conjuga TODOS los verbos del mensaje. */
export type TratamientoReal = "vos" | "tu" | "usted";
export type NivelEmojiReal = "ninguno" | "pocos" | "muchos";
export type LargoMensajeReal = "corto" | "completo";

/**
 * La voz de la marca: todo lo que decide CÓMO se escribe el mensaje, más allá del tono.
 * Antes esto vivía hardcodeado en las plantillas del backend (voseo fijo, 🍣 para cualquier
 * rubro, sin CTA ni firma) y la marca no podía tocarlo.
 */
export interface VozReal {
  tratamiento?: TratamientoReal;
  emojis?: NivelEmojiReal;
  largo?: LargoMensajeReal;
  /** un emoji del rubro; vacío = ninguno */
  emojiRubro?: string;
  /** qué querés que HAGA la persona. Vacío = el mensaje no pide nada. */
  cta?: string;
  /** quién firma. Vacío = sin firma. */
  firma?: string;
  /** palabras vetadas: si aparecen, el backend NO deja enviar (no las censura solo) */
  prohibidas?: string[];
}

/**
 * El programa de puntos, con números: cuántos se reparten y hasta cuándo valen.
 *
 * Antes `programaPuntos` era un booleano suelto que no cambiaba nada del mensaje. Un punto sin
 * cantidad no se puede prometer, y sin vencimiento no genera ninguna urgencia por volver.
 */
export interface PuntosConfigReal {
  /** cuántos puntos recibe CADA persona alcanzada. 0/ausente = no se menciona nada. */
  porPersona?: number;
  /** días hasta que vencen, contados DESDE EL ENVÍO. 0/ausente = no se promete fecha. */
  vigenciaDias?: number;
  /** cómo los llama la marca ("estrellas"). Vacío = "puntos". */
  nombre?: string;
}

export interface ContextReal {
  usaDescuentos: boolean;
  descuento: string;
  cuponBienvenida: boolean;
  canales: string;
  programaPuntos: boolean;
  /** cuántos puntos y hasta cuándo. Sólo se usa con `programaPuntos: true`. */
  puntos?: PuntosConfigReal;
  /** modo puja: el techo se fija en $ (deriva el techoPct). Sólo UI; el motor sigue en techoPct. */
  usaPuja?: boolean;
  /** el $ exacto que tipeó el dueño en la puja (el techoPct se deriva de esto; mostrarlo sin redondear) */
  pujaPorCliente?: number;
  tono: string;
  /** el resto de la voz. Ausente = defaults del backend (vos / pocos emojis / completo). */
  voz?: VozReal;
  techoPct: number;
  /** ausente = sin restricción horaria */
  franjaHoraria?: FranjaHorariaReal;
  /** ausente = el mensaje sólo dice el %, sin decir cómo canjearlo */
  cupon?: CuponConfigReal;
  /** días que mira la fatiga de ESTA campaña: un ajuste más estricto de la regla de la marca (oficial + QR, en
   *  WhatsApp → Configuración → Envío), que vale siempre. 0/ausente = sólo la regla de la marca (antes, «sin fatiga»). */
  fatigaDias?: number;
}

export interface StatsReal {
  contactos: number;
  /** a cuántos les escribe la corrida en curso, con los que ya tenían su mensaje entregado. Ausente si salió antes. */
  aEnviar?: number;
  enviados: number;
  volvieron: number;
  plataRecuperada: number;
  /** total sin despachar. Invariante del backend: pendientes = sinCupo + fueraDeFranja */
  pendientes?: number;
  /** esperan cupo: se libera MAÑANA (o conectando otra línea / subiendo el tope) */
  sinCupo?: number;
  /** fuera de la ventana horaria: se libera HOY cuando abre la franja. Otra causa, otra acción. */
  fueraDeFranja?: number;
  simulados?: number;
  fallidos?: number;
  /**
   * EVIDENCIA del gate legal (Ley 25.326), persistida a propósito: son las dos preguntas que el
   * cliente hace siempre — "¿por qué le mandaste a 800 si mi base tiene 1200?" y "¿con qué
   * derecho?". Vivían sólo en el response del envío y se perdían al cerrar el modal.
   */
  excluidosSinOptIn?: number;
  bloqueadosDnc?: number;
  /** la política vigente EN EL MOMENTO del envío (no la de ahora) */
  politicaOptIn?: "estricta" | "declarada";
  /** mails que el último envío dejó en la cola porque falta FRONTEND_URL: sin link de baja no salen (H03) */
  emailsSinLinkBaja?: number;
}

/** «qr»: una línea vinculada escaneando el QR. «api»: WhatsApp oficial. Espejo de `ViaWhatsApp` del backend. */
export type ViaWhatsApp = "qr" | "api";

/** Cuántos WhatsApp de un análisis salen por cada vía (`RepartoWhatsApp` del backend). Cada persona va por una sola. */
export interface RepartoWhatsApp {
  qr: number;
  api: number;
  asignadosQr: number;
  asignadosApi: number;
  definidoAt: string;
}

/** `GET /activa/campaigns/:id/reparto` (`EstadoReparto` de `reparto.service.ts`). */
export interface EstadoReparto {
  reparto: RepartoWhatsApp | null;
  /** a cuántos les puede llegar por cada vía hoy; `total` = personas distintas (el máximo de qr + api) */
  disponibles: { qr: number; api: number; total: number };
  asignados: { qr: number; api: number };
  /** por qué ya no se puede cambiar (salió una parte, está programada…), o null */
  bloqueo: string | null;
  /** la campaña de WhatsApp oficial de este análisis, para seguir la parte de la API */
  campanaOficial: { id: string; estado: string } | null;
}

export interface CampanaReal {
  id: string;
  brandId: string;
  nombre: string;
  marca: string;
  estado: EstadoCampana;
  periodo: string;
  fileHash?: string;
  shareToken?: string;
  report?: ReportReal;
  grupos: GrupoReal[];
  context?: ContextReal;
  stats: StatsReal;
  /**
   * Cuándo SALIERON los mensajes. NO es createdAt (que es cuándo se analizó la base): es la
   * fecha de corte de la atribución. Si falta, measure() cae en createdAt —que es ANTERIOR— y
   * puede contar como "volvió por la campaña" una compra hecha antes de recibir el mensaje.
   */
  enviadoAt?: string;
  /** alcance del primer envío (ausente = todos): reanudar no lo amplía, el backend lo aplica solo */
  enviadoGrupos?: string[];
  enviadoLineas?: string[];
  /** se armó con el dataset de ejemplo del wizard: se analiza y previsualiza, no se envía */
  esEjemplo?: boolean;
  /** los WhatsApp repartidos entre las líneas por QR y WhatsApp oficial (API); null/ausente = sin reparto */
  reparto?: RepartoWhatsApp | null;
  /**
   * Lo calcula el backend en listCampaigns con la MISMA fórmula que getResults: sólo
   * "exacta-por-snapshot" habilita mostrar plata recuperada. Sin esto, el portal del cliente
   * sumaba campañas sin medir y presentaba un 0 provisorio como si fuera una medición.
   */
  atribucion?: "sin-medir" | "pendiente-base-nueva" | "exacta-por-snapshot";
  /** cuándo TIENE que salir (estado "programada"). Lo dispara el scheduler del backend. */
  programadoPara?: string;
  /**
   * El scheduler la encontró demasiado tarde (el proceso estuvo caído) y NO la mandó sola:
   * un mensaje 8 horas tarde es peor que uno que no salió. La decide una persona.
   */
  programacionVencida?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Embudo agregado. `leidos` sólo tiene sentido en WhatsApp (✓✓ azul); en email no existe. */
export interface TotalResultados {
  enviados: number;
  entregados: number;
  leidos: number;
  respondieron: number;
  volvieron: number;
  /**
   * REAL: suma de lo que gastó cada persona DESPUÉS del envío (monto acumulado del snapshot
   * posterior − el del período de la campaña). Ya no es `volvieron × ticket promedio`.
   */
  plataRecuperada: number;
}

export interface GrupoResultados {
  grupoId: string;
  nombre: string;
  emoji: string;
  enviados: number;
  entregados: number;
  leidos: number;
  respondieron: number;
  volvieron: number;
  /** ESTIMACIÓN (ver TotalResultados.plataRecuperada) */
  plata: number;
}

export interface CanalResultados {
  /** mensajes de este canal, hayan salido o no. Es el denominador honesto. */
  total?: number;
  enviados: number;
  entregados: number;
  leidos: number;
  respondieron: number;
  rebotes: number;
  /** NO salieron (transporte simulado / SMTP sin configurar). Sin esto, un canal donde no salió
   *  nada queda todo en cero y parece un canal que no se usó. */
  simulados?: number;
}

export interface ResultadosReal {
  total: TotalResultados & {
    rebotes?: number;
    bajas?: number;
    /** no se despacharon: cupo diario agotado */
    pendientes?: number;
    /** NO SALIERON: transporte simulado / SMTP sin configurar. No son entregas. */
    simulados?: number;
    fallidos?: number;
  };
  porGrupo: GrupoResultados[];
  /** embudo separado por canal: promediarlos miente (en email no existe "leído") */
  porCanal?: Record<string, CanalResultados>;
  /** true si el estado es "measured" — NO implica que la atribución sea exacta */
  medida: boolean;
  /** true si existe una base de un período posterior (condición para medir de verdad) */
  hayPosterior?: boolean;
  /**
   * true = ALGUNO de estos números salió de un transporte simulado (WA_TRANSPORT=simulated o
   * SMTP sin configurar): NO son entregas reales y la UI tiene que decirlo, no maquillarlo.
   */
  hayDatosSimulados?: boolean;
  eventosSimulados?: number;
  /**
   * sin-medir = nunca se midió · pendiente-base-nueva = se corrió measure pero sin base
   * nueva (el 0 NO concluye nada) · exacta-por-snapshot = comparado persona por persona.
   */
  atribucion?: "sin-medir" | "pendiente-base-nueva" | "exacta-por-snapshot";
}

/** Una fila de la bitácora: el ESTADO ACTUAL del mensaje de una persona (no el historial). */
export interface EventoReal {
  id: string;
  personaId: string;
  grupo: string;
  canal: string;
  /** enmascarado por el backend salvo ?reveal=true */
  numeroDestino: string;
  lineaOrigen: string;
  mensaje: string;
  enviado: boolean;
  entregado: boolean;
  leido: boolean;
  /** "simulado" = NO salió nada (transporte simulado / SMTP sin configurar). Faltaba en el union
   *  justo el valor que importa: sin él, TypeScript no dejaba distinguir lo que no se envió. */
  estadoEntrega?: "pendiente" | "simulado" | "enviado" | "entregado" | "leido" | "fallido";
  /** por dónde salió REALMENTE. simulated/smtp_mock = no salió, aunque diga "enviado". */
  transporte?: "baileys" | "openwa" | "simulated" | "smtp" | "smtp_mock";
  waMessageId?: string;
  motivoError?: string;
  entregadoAt?: string;
  leidoAt?: string;
  rebote: boolean;
  intentos?: number;
  respondio: boolean;
  respuesta?: string;
  baja: boolean;
  volvioAComprar: boolean;
  createdAt: string;
}

export interface BitacoraReal {
  items: EventoReal[];
  total: number;
  page: number;
  limit: number;
  paginas: number;
  enmascarado: boolean;
}

export interface RespuestaReal {
  id: string;
  personaId: string;
  persona: string;
  grupo: string;
  respuesta: string;
  mensajeEnviado: string;
  estado: "baja" | "sin responder";
  linea: string;
  createdAt: string;
  /** de quién es la respuesta (`armarRespuestas` en el backend): el `persona` es el id del CSV, «K-10057» */
  nombre?: string;
  /** su celular o mail, enmascarado: el número completo no viaja a la pantalla */
  contactoVisible?: string;
  /** el nombre de la línea por la que entró (`linea` es su id) */
  lineaNombre?: string;
}

export interface LineaReal {
  id: string;
  nombre: string;
  estado: "desconectada" | "qr_pendiente" | "conectando" | "calentando" | "activa" | "bloqueada";
  msgsPorDia: number;
  enviadosHoy: number;
  /**
   * Día (YYYY-MM-DD, UTC) al que corresponde `enviadosHoy`. El backend resetea el contador recién
   * cuando despacha, así que a la mañana `enviadosHoy` todavía trae el consumo de AYER: sin
   * comparar contra este campo, la UI pinta 180/200 a las 9am y el operador cree que no le queda
   * cupo. Comparar como STRING contra `new Date().toISOString().slice(0,10)` — `new Date(cupoFecha)`
   * parsea a medianoche UTC y en UTC-3 daría el día anterior.
   */
  cupoFecha?: string;
  numero?: string;
  lastError?: string;
  conectada?: boolean;
}

/** Resultado de POST /campaigns/:id/measure */
export interface MedicionReal {
  volvieron: number;
  /** REAL: delta medido entre snapshots (lo que gastaron después del envío), no un promedio */
  plataRecuperada: number;
  contactados: number;
  atribucion: "exacta-por-snapshot" | "pendiente-base-nueva";
  pendiente: boolean;
  /** true = la plata es el delta medido, no un ticket promedio estimado */
  plataEsReal?: boolean;
  /** volvieron pero sin base previa para calcular cuánto gastaron (no se estima) */
  sinBaseComparable?: number;
  /** true = campaña sin `enviadoAt`: se usó la fecha del análisis y puede sobre-atribuir */
  fechaEnvioAproximada?: boolean;
}

/** Procedencia de un número en pantalla — se muestra al usuario, no es decorativo. */
export type Procedencia = "REAL" | "ESTIMADO" | "PENDIENTE";

/* ── Cuentas de email (el otro canal) ── */

/** `verificada` = la conexión SMTP real anduvo. Guardada ≠ puede enviar (como el QR de una línea). */
export type EstadoCuentaEmail = "sin_verificar" | "verificando" | "verificada" | "error";

export interface CuentaEmailReal {
  id: string;
  nombre: string;
  fromEmail: string;
  fromName: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  estado: EstadoCuentaEmail;
  predeterminada: boolean;
  verificadaAt?: string;
  lastError?: string;
  msgsPorDia: number;
  enviadosHoy: number;
  createdAt: string;
  // OJO: smtpPass NUNCA llega del backend (select:false + cifrada). No la tipamos a propósito.
}

/**
 * Qué se lleva la persona de un grupo, en una línea ("15% off" / "500 puntos" / "sin descuento").
 *
 * Vive acá y no en cada pantalla porque estaba escrito TRES veces (paso 8, wizard viejo, detalle
 * de campaña) y las tres decían `descuentoPct > 0 ? "…% off" : "sin descuento"`. Con el programa
 * de puntos encendido, eso etiqueta como "sin descuento" al grupo al que le estamos repartiendo
 * 12.500 puntos — la etiqueta contradice al mensaje que la propia pantalla muestra abajo.
 */
export function etiquetaOferta(g: { descuentoPct?: number; puntosPorPersona?: number; oferta?: string }): string {
  if (Number(g.descuentoPct) > 0) return `${g.descuentoPct}% off`;
  // el backend ya arma el texto ("500 puntos", con el nombre que la marca eligió)
  if (Number(g.puntosPorPersona) > 0) return String(g.oferta || "").trim() || "con puntos";
  return "sin descuento";
}
